import { createSelector, Dispatch } from '@reduxjs/toolkit';
import {
    selectActivityContentLocale,
    selectActivityFromDetailState,
    selectActivityImagesFromDetailState,
    selectReviewSortAndFilterMatrix,
} from 'component/page/detail/store/detail.selectors';
import { selectCmsFromBootstrap, selectFeatureBehaviorFromBootstrap } from 'store/slice/bootstrap';
import { navigateToHashWithTabBarOffset } from 'component/common/DetailInViewWrapper/navigateToHashWithTabBarOffset';
import { CmsValue } from 'core/type/BootstrapConfig.types';
import { getReviewInfo } from 'component/page/detail/Review/ReviewSortAndFilter/ReviewSortAndFilter.utils';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesSeeMoreDetailsClickEventActivitiesFieldsV1,
    ActivitiesSeeReviewTextlinkClickEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesSeeMoreDetailsClickEventActivitiesManifestV1,
    ActivitiesSeeReviewTextlinkClickEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { mapTitleFromContent } from 'gql/service/graph/activityService';
import { setIsScrolling } from 'component/page/detail/store/details';
import { Badges } from 'gql/autogen/Badges';
import { ScreenSize, selectScreenSize } from 'store/slice/screen';
import { TitleSectionSpecificProps } from 'component/page/detail/DetailPageModularize/detailPageModularize.types';
import { FeatureName } from 'core/constant/FeatureName';
import { isOn } from 'core/window/bootstrapConfig';
import { OverlayType, showOverlay } from 'store/slice/overlay';
import { Image } from 'gql/autogen/Image';

interface DetailCardStateProps {
    title: string;
    contentLocale: string;
    cms: CmsValue;
    badges: Badges | undefined;
    activityId: number;
    categoryId: string | undefined;
    screenSize: ScreenSize;
    reviewTotalReview: number;
    reviewAverageScore: string;
    pageContainerSpacing: boolean;
    mainImage?: Image;
}

interface DetailCardDispatchProps {
    onSeeReviewClick: (seeReviewHref: string) => void;
    showSeeDetailsOverlay: () => void;
}

interface OwnProps {
    seeReviewHref: string;
}

export type DetailsCardProps = OwnProps & DetailCardStateProps & DetailCardDispatchProps;

export type DetailSectionModularProps = DetailCardStateProps & TitleSectionSpecificProps & DetailCardDispatchProps;

export const mapStateToProps = createSelector(
    selectActivityFromDetailState,
    selectActivityContentLocale,
    selectCmsFromBootstrap,
    selectReviewSortAndFilterMatrix,
    selectActivityImagesFromDetailState,
    selectScreenSize,
    selectFeatureBehaviorFromBootstrap,
    (activity, contentLocale, cms, reviewSortAndFilterMatrix, images, screenSize, feature): DetailCardStateProps => {
        // Detail endpoint data
        const activityContent = activity.content || {};
        const title = mapTitleFromContent(activityContent);
        const badges = activityContent?.badges;
        const activityId = activity.masterActivityId || 0;
        const categoryId = activityContent?.activity?.categories?.[0];
        // Review endpoint data
        const reviewTotalAndAverageScore = getReviewInfo(contentLocale, cms, reviewSortAndFilterMatrix);
        const mainImage = isOn(FeatureName.DetailPageForSimCard) ? images[0] : undefined;
        return {
            title,
            contentLocale,
            cms,
            badges,
            activityId,
            categoryId,
            screenSize,
            mainImage,
            reviewTotalReview: reviewTotalAndAverageScore.totalReview,
            reviewAverageScore: reviewTotalAndAverageScore.averageScore,
            pageContainerSpacing: feature.PageContainerSpacing || false,
        };
    },
);

export const mapDispatchToProps = (dispatch: Dispatch): DetailCardDispatchProps => ({
    onSeeReviewClick: (seeReviewHref: string) => {
        trackElementEvent<ActivitiesSeeReviewTextlinkClickEventActivitiesFieldsV1>(ActivitiesSeeReviewTextlinkClickEventActivitiesManifestV1.ids, {});
        navigateToHashWithTabBarOffset(dispatch, `#${seeReviewHref}`, setIsScrolling, true);
    },
    showSeeDetailsOverlay: () => {
        trackElementEvent<ActivitiesSeeMoreDetailsClickEventActivitiesFieldsV1>(ActivitiesSeeMoreDetailsClickEventActivitiesManifestV1.ids, {});
        dispatch(showOverlay(OverlayType.InfoOverlay));
    },
});
